import {Card, openDialog, showNotification} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {last} from 'ramda';
import {isNilOrEmpty, isString} from 'ramda-adjunct';

import {
  ConvertForAllVehiclesApiArg,
  UpdateInterestApiArg,
  useConvertForAllVehiclesMutation,
  useGetInterestV2Query,
  useGetMinipurchaseQuery,
  useUpdateInterestMutation,
} from '@dms/api/finance';
import {useGetVehicleQuery} from '@dms/api/sales';
import {ApiError} from '@dms/api/shared';
import {ConditionEnum} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {interestRoutes, testIds} from '@dms/routes';
import {
  DmsDataGrid,
  handleApiError,
  InterestEndCaseDialog,
  interestSearchParams,
  queryParams,
  useLazyGetAuthorizationProfilesAndCKKPermissions,
  useMandatoryFields,
  useMandatoryFieldsErrorDialog,
} from '@dms/shared';

import {composePath, useNavigate, useRequiredParams} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

export function VehicleInterestsCard() {
  const {id} = useRequiredParams();
  const navigate = useNavigate();

  const [updateInterest] = useUpdateInterestMutation();
  const [convertForAllVehicles] = useConvertForAllVehiclesMutation();

  const {isMandatoryFieldsEnabled} = useMandatoryFields();

  const {data: interest} = useGetInterestV2Query({id});
  const {handleSearchAuthorizationProfiles} = useLazyGetAuthorizationProfilesAndCKKPermissions();
  const miniPurchaseId = interest?.minipurchasesIds ? last(interest.minipurchasesIds) : null;
  const {data: miniPurchase} = useGetMinipurchaseQuery(
    {minipurchaseId: miniPurchaseId ?? ''},
    {
      skip: isNilOrEmpty(miniPurchaseId),
    }
  );
  const vehicleId = miniPurchase?.offeredVehiclesIds?.[0];
  const {data: vehicleData} = useGetVehicleQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: isNilOrEmpty(vehicleId)}
  );

  const {handleMandatoryFieldsError, isMandatoryFieldsError} =
    useMandatoryFieldsErrorDialog(vehicleId);

  const queryModifier = (filter: QueryFilterObject) => ({
    ...filter,
    vehicle: [id],
  });

  const actionCallback: ActionCallback = ({actionKey, rowId, sourceSearchParams, refreshData}) => {
    if (!isString(rowId)) {
      showNotification.error();
      return;
    }

    match(actionKey)
      .with('detail', () =>
        navigate(
          composePath(interestRoutes.detail, {
            params: {id: rowId},
            isSearchParamsPreserved: false,
            queryParams: `${sourceSearchParams}&${[queryParams.COMPONENT_SECTIONS_SECTION]}=${interestSearchParams.interestDetail.overview}`,
          })
        )
      )
      .with('open', () => {
        const args: UpdateInterestApiArg = {
          id: rowId,
          updateInterestRequestBody: {
            state: 'OPEN',
          },
        };
        updateInterest(args)
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('end', () =>
        openDialog(
          <InterestEndCaseDialog
            data-testid={testIds.interest.overview('endCaseDialog')}
            interestId={rowId}
            onSubmit={refreshData}
          />,
          {
            'data-testid': testIds.interest.overview('endCaseDialog'),
            title: i18n.t('entity.interest.labels.endInterestCase'),
            scrollBehavior: 'outside',
          }
        )
      )
      .with('createBusinessCase', () => {
        const args: ConvertForAllVehiclesApiArg = {
          interestId: rowId ?? '',
          validateMandatoryFields: isMandatoryFieldsEnabled(
            (vehicleData?.vehicle?.condition as ConditionEnum) ?? undefined
          ),
        };
        convertForAllVehicles(args)
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch((error: ApiError) => {
            if (isMandatoryFieldsError(error)) {
              return handleMandatoryFieldsError(error.data?.validationErrors ?? []);
            }

            return handleApiError(error);
          });
        handleSearchAuthorizationProfiles(
          {
            vehicleMake: vehicleData?.make ?? '',
            vehicleType: vehicleData?.type ?? null,
            vehicleModelFamily: vehicleData?.vehicle?.modelFamily ?? null,
            vehicleModelFamilyGroup: vehicleData?.vehicle?.modelFamilyGroup ?? null,
          },
          interest?.customerId ?? undefined
        );
      })
      .otherwise(() => {
        showNotification.warning(`Action callback was not specified for action ${actionKey}`);
      });
  };

  return (
    <>
      <Card title={i18n.t('page.interests.title')}>
        <Box height={80}>
          <DmsDataGrid
            gridCode="vehicle-interests-list"
            actionCallback={actionCallback}
            queryModifier={queryModifier}
            data-testid={testIds.vehicles.interests('vehicle-interests-card-datagrid')}
          />
        </Box>
      </Card>
    </>
  );
}
